import React, { useState, useEffect } from 'react';
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Cookie, Settings, Shield, BarChart3, X } from 'lucide-react';

interface CookieConsentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAcceptAll: () => void;
  onAcceptEssential: () => void;
  onCustomize: () => void;
}

const CookieConsentModal: React.FC<CookieConsentModalProps> = ({
  isOpen,
  onClose,
  onAcceptAll,
  onAcceptEssential,
  onCustomize
}) => {
  const [showDetails, setShowDetails] = useState(false);
  const [preferences, setPreferences] = useState({
    essential: true,
    analytics: false,
    marketing: false,
    functionality: false
  });

  const cookieTypes = [
    {
      id: 'essential',
      name: 'Essential Cookies',
      description: 'Required for the website to function properly. These cannot be disabled.',
      icon: Shield,
      required: true,
      examples: ['Session management', 'Security', 'Cart functionality']
    },
    {
      id: 'analytics',
      name: 'Analytics Cookies',
      description: 'Help us understand how you use our website to improve your experience.',
      icon: BarChart3,
      required: false,
      examples: ['Google Analytics', 'Page views', 'User behavior']
    },
    {
      id: 'marketing',
      name: 'Marketing Cookies',
      description: 'Used to show you relevant ads and track campaign effectiveness.',
      icon: Cookie,
      required: false,
      examples: ['Google Ads', 'Facebook Pixel', 'Retargeting']
    },
    {
      id: 'functionality',
      name: 'Functionality Cookies',
      description: 'Remember your preferences and settings for a better experience.',
      icon: Settings,
      required: false,
      examples: ['Language preferences', 'Theme settings', 'Location data']
    }
  ];

  const handleAcceptAll = () => {
    setPreferences({
      essential: true,
      analytics: true,
      marketing: true,
      functionality: true
    });
    onAcceptAll();
  };

  const handleAcceptEssential = () => {
    setPreferences({
      essential: true,
      analytics: false,
      marketing: false,
      functionality: false
    });
    onAcceptEssential();
  };

  const handleSavePreferences = () => {
    // Save preferences to localStorage
    localStorage.setItem('cookiePreferences', JSON.stringify(preferences));

    // Initialize Google Analytics if analytics are enabled
    if (preferences.analytics) {
      // Initialize GA4 here
      console.log('Analytics cookies accepted');
    }

    onCustomize();
  };

  const handleTogglePreference = (type: keyof typeof preferences) => {
    if (type === 'essential') return; // Cannot disable essential cookies

    setPreferences(prev => ({
      ...prev,
      [type]: !prev[type]
    }));
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center">
                <Cookie className="w-6 h-6 text-amber-600" />
              </div>
              <div>
                <DialogTitle className="text-xl font-bold">Cookie Preferences</DialogTitle>
                <DialogDescription className="text-gray-600">
                  We use cookies to enhance your experience and analyze our website traffic.
                </DialogDescription>
              </div>
            </div>
            <Button variant="ghost" size="sm" onClick={onClose}>
              <X className="w-4 h-4" />
            </Button>
          </div>
        </DialogHeader>

        <div className="space-y-6">
          {!showDetails ? (
            <>
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <h3 className="font-semibold text-amber-800 mb-2">🍪 We respect your privacy</h3>
                <p className="text-sm text-amber-700 leading-relaxed">
                  We use cookies to improve your browsing experience, analyze website traffic, and understand where our visitors are coming from.
                  By clicking "Accept All", you consent to our use of cookies.
                </p>
              </div>

              <div className="grid md:grid-cols-3 gap-3">
                <Button
                  onClick={handleAcceptAll}
                  className="bg-gradient-to-r from-amber-600 to-orange-600 hover:from-amber-700 hover:to-orange-700 text-white"
                >
                  Accept All
                </Button>
                <Button
                  onClick={handleAcceptEssential}
                  variant="outline"
                  className="border-gray-300"
                >
                  Essential Only
                </Button>
                <Button
                  onClick={() => setShowDetails(true)}
                  variant="outline"
                  className="border-amber-300 text-amber-700 hover:bg-amber-50"
                >
                  <Settings className="w-4 h-4 mr-2" />
                  Customize
                </Button>
              </div>

              <div className="text-xs text-gray-500 space-y-2">
                <p>
                  <strong>Essential cookies</strong> are always enabled to ensure the website functions properly.
                </p>
                <p>
                  You can change your preferences at any time by clicking the cookie icon in the footer.
                </p>
              </div>
            </>
          ) : (
            <>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold">Customize Cookie Settings</h3>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowDetails(false)}
                    className="text-gray-500"
                  >
                    ← Back
                  </Button>
                </div>

                <div className="space-y-4">
                  {cookieTypes.map((type) => (
                    <div key={type.id} className="border border-gray-200 rounded-lg p-4">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center">
                            <type.icon className="w-4 h-4 text-gray-600" />
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <h4 className="font-semibold text-gray-900">{type.name}</h4>
                              {type.required && (
                                <Badge variant="secondary" className="text-xs">Required</Badge>
                              )}
                            </div>
                            <p className="text-sm text-gray-600 mt-1">{type.description}</p>
                          </div>
                        </div>

                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            className="sr-only peer"
                            checked={preferences[type.id as keyof typeof preferences]}
                            onChange={() => handleTogglePreference(type.id as keyof typeof preferences)}
                            disabled={type.required}
                          />
                          <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-amber-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-amber-600"></div>
                        </label>
                      </div>

                      <div className="text-xs text-gray-500">
                        <strong>Examples:</strong> {type.examples.join(', ')}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex gap-3 pt-4 border-t">
                <Button
                  onClick={handleSavePreferences}
                  className="flex-1 bg-gradient-to-r from-amber-600 to-orange-600 hover:from-amber-700 hover:to-orange-700 text-white"
                >
                  Save Preferences
                </Button>
                <Button
                  onClick={handleAcceptAll}
                  variant="outline"
                  className="border-gray-300"
                >
                  Accept All
                </Button>
              </div>
            </>
          )}
        </div>

        <div className="text-xs text-gray-500 pt-4 border-t">
          <p>
            By continuing to use our website, you agree to our use of cookies as described in our{' '}
            <button
              onClick={() => window.open('/privacy', '_blank')}
              className="text-amber-600 hover:underline"
            >
              Privacy Policy
            </button>
            . For more information about the cookies we use, please see our{' '}
            <button
              onClick={() => window.open('/privacy#cookies', '_blank')}
              className="text-amber-600 hover:underline"
            >
              Cookie Policy
            </button>
            .
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CookieConsentModal;